// src/components/padrekko/qc/ShareCaseModal.jsx
//
// REM-576 — the owner shares the case with the respondent (the operator's
// org), choosing the response deadline.
//
// THE DEADLINE IS REQUIRED AND NOTHING IS PRESELECTED. The deadline is a term
// of the share (REM-600): it is set here and cannot be edited, only replaced
// by revoking and sharing again. A preselected option would be a date the
// owner did not choose. The store refuses to send a share without one.
//
// THE PREVIEW SAYS "ABOUT". It is resolveResponseDeadline (the server's own
// function, imported) applied to the browser's clock now; the server applies
// it to its clock at the moment of the share. They agree unless the share
// lands across a UTC midnight from the preview.

import { useState } from "react";
import { Loader2, X as XIcon, Share2 } from "lucide-react";
import { useQcStore } from "../../../stores/useQcStore";
import { toast } from "../../../stores/useToastStore";
import {
  RESPONSE_DEADLINE_DAYS,
  resolveResponseDeadline,
} from "../../../../../admin-api/lib/padrekko-qc/caseStages.js";
import ActionError from "./ActionError";

const BASES = Object.keys(RESPONSE_DEADLINE_DAYS);

export default function ShareCaseModal({ isOpen, onClose, operatorName }) {
  const caseInfo      = useQcStore((s) => s.caseInfo);
  const shareCase     = useQcStore((s) => s.shareCase);
  const shareInflight = useQcStore((s) => s.shareInflight);

  const [basis, setBasis] = useState(null);
  const [failure, setFailure] = useState(null);

  if (!isOpen || !caseInfo) return null;

  const preview = basis ? resolveResponseDeadline(basis, new Date()) : null;

  function close() {
    if (shareInflight) return;
    setBasis(null);
    setFailure(null);
    onClose();
  }

  async function submit() {
    if (!basis) return;
    setFailure(null);
    const r = await shareCase({ deadlineBasis: basis });
    if (!r.ok) { setFailure(r); return; }
    const due = r.share?.response_deadline_date ?? preview;
    toast.success(`Shared with ${operatorName}; due ${due}`);
    setBasis(null);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && close()}
      role="dialog"
      aria-modal="true"
      aria-label={`Share with ${operatorName}`}
    >
      <div className="w-full max-w-md bg-bg-soft border border-slate-700 rounded-xl shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-100 flex items-center gap-1.5">
            <Share2 size={12} className="text-emerald-400" />
            Share with {operatorName}
          </h3>
          <button
            type="button"
            onClick={close}
            disabled={shareInflight}
            className="text-fg-secondary hover:text-slate-200 transition disabled:opacity-40"
            title="Cancel"
            aria-label="Cancel"
          >
            <XIcon size={14} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-[11px] text-slate-300 leading-relaxed">
            {operatorName} will be able to open this case and respond to its findings.
          </p>

          <fieldset className="space-y-1.5">
            <legend className="text-[10px] font-mono uppercase tracking-widest text-fg-secondary">
              Response deadline (required)
            </legend>
            <div className="flex flex-wrap gap-3">
              {BASES.map((b) => (
                <label key={b} className="flex items-center gap-1 text-[12px] text-slate-200 cursor-pointer">
                  <input
                    type="radio"
                    name="response-deadline"
                    value={b}
                    checked={basis === b}
                    onChange={() => { setBasis(b); setFailure(null); }}
                    disabled={shareInflight}
                  />
                  {RESPONSE_DEADLINE_DAYS[b]} days
                </label>
              ))}
            </div>
            {preview && (
              <p className="text-[11px] text-emerald-200/90" data-qc-share-preview>
                Due about {preview} (UTC), counted from the moment you share.
              </p>
            )}
          </fieldset>

          <p className="text-[11px] text-fg-secondary leading-relaxed">
            The response deadline is a term of the share. To change it later, you
            revoke the share and share again with a new deadline.
          </p>

          <ActionError failure={failure} />
        </div>

        <div className="px-5 py-3 border-t border-slate-800 flex justify-end gap-1.5">
          <button
            type="button"
            onClick={close}
            disabled={shareInflight}
            className="text-[11px] px-3 py-1.5 rounded border border-slate-700 text-slate-300
                       hover:bg-slate-800 hover:border-slate-600 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!basis || shareInflight}
            className="flex items-center gap-1 text-[11px] px-3 py-1.5 rounded border
                       bg-emerald-500/15 border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/25
                       disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {shareInflight && <Loader2 size={11} className="animate-spin" />}
            Share
          </button>
        </div>
      </div>
    </div>
  );
}
